/**
 * Sidebar — fixed navigation shell
 *
 * Premium features:
 * - Collapsible (icon-only at 52px, full at 184px) with smooth transition
 * - Workspace switcher popover
 * - Keyboard shortcut hints on hover
 * - Consistent icon family at strokeWidth 1.75 (active: 2)
 * - Notification dot on My Work when tasks are overdue
 *
 * Account/profile actions (Profile, Workspace settings, Billing, Help,
 * Sign out) live in the top-right ProfileDropdown (CommandRow.tsx) — the
 * single source of truth, not duplicated here.
 */

import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MyEmployeeViewLink } from '../WorkspaceSwitch'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../contexts/ProfileContext'
import { useTenant } from '../../contexts/TenantContext'
import { useWorkspaceAccess } from '../../contexts/WorkspaceAccessContext'
import { isGlobalSuperadmin } from '../../lib/authz'
import { LEAVE_ENABLED } from '../../flags'
import {
  LayoutDashboard,
  Users,
  Network,
  CheckSquare,
  Shield,
  Settings,
  Layers,
  AlertCircle,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Building2,
  ChevronsUpDown,
  CalendarDays,
} from 'lucide-react'
import { BrandLogo } from '../BrandLogo'

export const SIDEBAR_W          = 184
export const SIDEBAR_W_COLLAPSED = 52

interface SidebarProps {
  onNavigate?: () => void
  taskCount?: number
  overdueComplianceCount?: number
  tenantName?: string
  environmentLabel?: string
  onSupportClick?: () => void
  collapsed?: boolean
  onCollapsedChange?: (c: boolean) => void
}

interface NavItem {
  label:    string
  href:     string
  icon:     React.ElementType
  badge:    string | null
  alert:    boolean
  dot:      boolean
  separator?: boolean
  shortcut: string
}

// ── Shortcut hint chip ──────────────────────────────────────────────────────

function ShortcutChip({ keys }: { keys: string }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        opacity: 0,
        transition: 'opacity 120ms ease',
        pointerEvents: 'none',
      }}
      className="shortcut-chip"
    >
      {keys.split('+').map((k, i) => (
        <kbd
          key={i}
          style={{
            fontSize: 9,
            fontWeight: 600,
            lineHeight: 1,
            padding: '1px 3px',
            borderRadius: 3,
            background: 'rgba(0,0,0,0.06)',
            color: '#94A3B8',
            fontFamily: 'inherit',
          }}
        >
          {k}
        </kbd>
      ))}
    </span>
  )
}

// ── Floating popover shell (renders fixed, to the right of sidebar) ─────────

function FloatingPopover({
  sidebarW,
  topOffset,
  bottomOffset,
  onClose,
  children,
}: {
  sidebarW: number
  topOffset?: number
  bottomOffset?: number
  onClose: () => void
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: sidebarW + 8,
        ...(topOffset !== undefined ? { top: topOffset } : {}),
        ...(bottomOffset !== undefined ? { bottom: bottomOffset } : {}),
        zIndex: 9999,
        width: 232,
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  )
}

// ── Workspace popover content ───────────────────────────────────────────────

function WorkspacePopover({
  workspaceName,
  planLabel,
  planRaw,
  sidebarW,
  seatsUsed,
  seatsIncluded,
  isActive,
  onClose,
}: {
  workspaceName: string
  planLabel: string
  planRaw: string
  sidebarW: number
  seatsUsed: number
  seatsIncluded: number
  isActive: boolean
  onClose: () => void
}) {
  // Founder plan = unlimited seats. Also treat Infinity / 0 as unlimited.
  const isUnlimited = planRaw === 'founder' || seatsIncluded === Infinity || seatsIncluded === 0
  const seatPct     = isUnlimited ? 0 : Math.min(100, Math.round((seatsUsed / seatsIncluded) * 100))
  const seatBarColor = seatPct >= 90 ? '#EF4444' : seatPct >= 70 ? '#F59E0B' : '#059669'

  return (
    <FloatingPopover sidebarW={sidebarW} topOffset={8} onClose={onClose}>
      {/* Identity */}
      <div style={{ padding: '14px 14px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            background: '#1F2A44', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
              {workspaceName.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 13.5, fontWeight: 600, color: '#0F172A', margin: 0, lineHeight: 1.2 }}>
              {workspaceName}
            </p>
            {/* Plan badge + status dot */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5 }}>
              {isActive && (
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: '#10B981', flexShrink: 0,
                }} />
              )}
              <span style={{
                fontSize: 10.5, fontWeight: 600,
                padding: '2px 6px', borderRadius: 4,
                background: '#F1F5F9', color: '#475569',
              }}>
                {planLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Seat usage */}
        <div style={{ marginTop: 12 }}>
          {isUnlimited ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#94A3B8' }}>
                {seatsUsed} active seat{seatsUsed !== 1 ? 's' : ''}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                background: '#F0FAF5', color: '#059669',
              }}>
                Unlimited
              </span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: '#94A3B8' }}>Seats</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: seatBarColor, tabularNums: true } as React.CSSProperties}>
                  {seatsUsed} / {seatsIncluded}
                </span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: '#F1F5F9', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  width: `${seatPct}%`,
                  background: seatBarColor,
                  transition: 'width 400ms ease',
                }} />
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ height: 1, background: '#F1F5F9' }} />

      <div style={{ padding: '6px' }}>
        <Link
          to="/settings?tab=company"
          onClick={onClose}
          style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '8px 10px', borderRadius: 8,
            textDecoration: 'none', color: '#334155', fontSize: 13, fontWeight: 500,
            transition: 'background 100ms',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#F8FAFC' }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '' }}
        >
          <Building2 style={{ width: 14, height: 14, color: '#94A3B8', strokeWidth: 1.75 }} />
          Workspace settings
        </Link>
      </div>
    </FloatingPopover>
  )
}

// ── Main component ──────────────────────────────────────────────────────────

export default function Sidebar({
  onNavigate,
  taskCount = 0,
  overdueComplianceCount = 0,
  tenantName,
  environmentLabel = 'Live',
  onSupportClick,
  collapsed = false,
  onCollapsedChange,
}: SidebarProps) {
  const { authState }               = useAuth()
  const { profileState, profile }   = useProfile()
  const { tenant }                  = useTenant()
  const { planName, workspaceStatus, seatsUsed, seatsIncluded } = useWorkspaceAccess()
  const location                    = useLocation()
  const isSuperadmin                = isGlobalSuperadmin(profile)

  const [workspaceOpen, setWorkspaceOpen] = useState(false)

  const isLoading = authState === 'loading' || profileState === 'loading' || profileState === 'idle'
  const hasError  = authState === 'error'   || profileState === 'error'

  const w = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W

  const navItems: NavItem[] = [
    { label: 'Overview',    href: '/dashboard',   icon: LayoutDashboard, badge: null, alert: false, dot: false, shortcut: 'G+O' },
    { label: 'People',      href: '/people',      icon: Users,           badge: null, alert: false, dot: false, shortcut: 'G+P' },
    { label: 'Organisation', href: '/organisation', icon: Network,        badge: null, alert: false, dot: false, shortcut: 'G+R' },
    { label: 'Blueprints',  href: '/blueprints',  icon: Layers,          badge: null, alert: false, dot: false, shortcut: 'G+B' },
    { label: 'My Work',     href: '/my-work',     icon: CheckSquare,     badge: null, alert: false, dot: taskCount > 0, shortcut: 'G+W' },
    { label: 'Analytics',   href: '/posture',     icon: TrendingUp,      badge: null, alert: false, dot: false, shortcut: 'G+A' },
    { label: 'Compliance',  href: '/compliance',  icon: Shield,          badge: null, alert: overdueComplianceCount > 0, dot: false, shortcut: 'G+C' },
    // Absent, not greyed out, when leave is off. A disabled nav item claims the
    // feature exists and you may not have it — the wrong thing to say about
    // something that is simply not switched on yet.
    ...(LEAVE_ENABLED
      ? [{ label: 'Time off', href: '/time-off', icon: CalendarDays, badge: null, alert: false, dot: false, shortcut: 'G+T' } as NavItem]
      : []),
    { label: 'Settings',    href: '/settings',    icon: Settings,        badge: null, alert: false, dot: false, shortcut: 'G+,', separator: true },
  ]

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard' && !location.search.includes('tab=')
    return location.pathname + location.search === href || location.pathname === href
  }

  const shell = (children: React.ReactNode) => (
    <div
      className="sidebar-root"
      style={{
        width: w,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#F3F5F9',
        borderRight: '1px solid rgba(0,0,0,0.06)',
        transition: 'width 200ms cubic-bezier(0.4,0,0.2,1)',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {children}
    </div>
  )

  if (isLoading) {
    return shell(
      <>
        <div style={{ height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', paddingLeft: 12, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ width: 24, height: 24, borderRadius: 7, background: '#DDE0E8' }} className="skeleton" />
          {!collapsed && <div style={{ width: 80, height: 11, borderRadius: 4, background: '#DDE0E8', marginLeft: 8 }} className="skeleton" />}
        </div>
        <div style={{ flex: 1, padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ height: 30, borderRadius: 6, background: '#DDE0E8' }} className="skeleton" />
          ))}
        </div>
      </>
    )
  }

  if (hasError) {
    return shell(
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AlertCircle style={{ width: 16, height: 16, color: '#F87171' }} />
      </div>
    )
  }

  const isOwnerOrAdmin = profile?.role === 'owner' || profile?.role === 'admin'
  if (!profile || (!isSuperadmin && !isOwnerOrAdmin)) return null

  const displayWorkspace = tenantName || tenant?.name || 'Workspace'
  const planLabel = planName
    ? `${planName.charAt(0).toUpperCase() + planName.slice(1)} Plan`
    : workspaceStatus === 'active' ? 'Active Plan' : 'Free Plan'

  return shell(
    <>
      {/* ── Workspace header ──────────────────────────────────────────────── */}
      <div
        style={{
          height: 48, flexShrink: 0,
          display: 'flex', alignItems: 'center',
          paddingLeft: 10, paddingRight: 10,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={() => { if (!collapsed) setWorkspaceOpen(o => !o) }}
          style={{
            flex: 1, display: 'flex', alignItems: 'center',
            gap: 8,
            minWidth: 0, border: 'none', background: 'none',
            cursor: collapsed ? 'default' : 'pointer',
            borderRadius: 7, padding: '5px 6px',
            textAlign: 'left', transition: 'background 120ms',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
          onMouseEnter={e => { if (!collapsed) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.05)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '' }}
          aria-label="Workspace menu"
        >
          <BrandLogo variant="mark" size="sm" />
          {!collapsed && (
            <>
              <span style={{
                flex: 1, minWidth: 0,
                fontSize: 13, fontWeight: 600, letterSpacing: '-0.018em',
                color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {displayWorkspace}
              </span>
              <ChevronsUpDown style={{ width: 13, height: 13, color: '#94A3B8', flexShrink: 0, strokeWidth: 1.75 }} />
            </>
          )}
        </button>

        {/* Workspace popover */}
        {workspaceOpen && !collapsed && (
          <WorkspacePopover
            workspaceName={displayWorkspace}
            planLabel={planLabel}
            planRaw={planName ?? ''}
            sidebarW={w}
            seatsUsed={seatsUsed}
            seatsIncluded={seatsIncluded}
            isActive={workspaceStatus === 'active'}
            onClose={() => setWorkspaceOpen(false)}
          />
        )}
      </div>

      {/* ── Collapse tab — hover-reveal on right edge ─────────────────────── */}
      {onCollapsedChange && (
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            position: 'absolute',
            right: -11,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 50,
            width: 22, height: 22,
            border: '1px solid #E2E8F0',
            background: '#FFFFFF',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#94A3B8',
            boxShadow: '0 1px 4px rgba(15,23,42,0.10)',
            transition: 'color 100ms, box-shadow 100ms',
            opacity: 0,
          }}
          className="sidebar-collapse-btn"
          onMouseEnter={e => {
            const el = e.currentTarget as HTMLElement
            el.style.color = '#1E293B'
            el.style.boxShadow = '0 2px 8px rgba(15,23,42,0.16)'
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLElement
            el.style.color = '#94A3B8'
            el.style.boxShadow = '0 1px 4px rgba(15,23,42,0.10)'
          }}
        >
          {collapsed
            ? <ChevronRight style={{ width: 11, height: 11, strokeWidth: 2 }} />
            : <ChevronLeft  style={{ width: 11, height: 11, strokeWidth: 2 }} />
          }
        </button>
      )}

      {/* ── Navigation ──────────────────────────────────────────────────────── */}
      <style>{`
        .nav-item:hover .shortcut-chip { opacity: 1 !important; }
        .sidebar-root:hover .sidebar-collapse-btn { opacity: 1 !important; }
      `}</style>
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }} aria-label="Main navigation">
        <ul role="list" style={{
          listStyle: 'none', margin: 0,
          padding: '6px 6px',
          display: 'flex', flexDirection: 'column', gap: 1,
        }}>
          {navItems.map((item) => {
            const Icon   = item.icon
            const active = isActive(item.href)

            return (
              <li key={item.href}>
                {item.separator && (
                  <div aria-hidden style={{ height: 1, margin: '4px 2px 5px', background: 'rgba(0,0,0,0.06)' }} />
                )}
                <Link
                  to={item.href}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className="nav-item"
                  title={collapsed ? item.label : undefined}
                  style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center',
                    height: 30,
                    paddingLeft: collapsed ? 0 : 9,
                    paddingRight: collapsed ? 0 : 7,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    gap: 7,
                    borderRadius: 6,
                    textDecoration: 'none',
                    background: active ? 'rgba(15,23,42,0.07)' : 'transparent',
                    color: active ? '#1E293B' : '#657181',
                    transition: 'background 110ms ease, color 110ms ease',
                  }}
                  onMouseEnter={e => {
                    if (!active) {
                      const el = e.currentTarget as HTMLElement
                      el.style.background = 'rgba(15,23,42,0.04)'
                      el.style.color = '#3D4F63'
                    }
                  }}
                  onMouseLeave={e => {
                    if (!active) {
                      const el = e.currentTarget as HTMLElement
                      el.style.background = 'transparent'
                      el.style.color = '#657181'
                    }
                  }}
                >
                  {/* Active strip */}
                  <span aria-hidden style={{
                    position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                    width: 2, height: 14, borderRadius: '0 2px 2px 0',
                    background: '#1F2A44',
                    opacity: active ? 1 : 0,
                    transition: 'opacity 150ms ease',
                    pointerEvents: 'none',
                  }} />

                  {/* Icon */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Icon aria-hidden style={{
                      width: 14, height: 14,
                      strokeWidth: active ? 2 : 1.75,
                      color: active ? '#1F2A44' : '#8898AA',
                      transition: 'color 110ms ease',
                    }} />
                    {/* Notification dot */}
                    {item.dot && (
                      <span style={{
                        position: 'absolute', top: -2, right: -2,
                        width: 5, height: 5, borderRadius: '50%',
                        background: '#EF4444',
                        border: '1px solid #F3F5F9',
                      }} />
                    )}
                    {/* Alert dot */}
                    {item.alert && (
                      <span style={{
                        position: 'absolute', top: -2, right: -2,
                        width: 5, height: 5, borderRadius: '50%',
                        background: '#EF4444',
                        border: '1px solid #F3F5F9',
                      }} />
                    )}
                  </div>

                  {/* Label + shortcut */}
                  {!collapsed && (
                    <>
                      <span style={{
                        flex: 1,
                        fontSize: 12.5,
                        fontWeight: active ? 600 : 500,
                        letterSpacing: '-0.008em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: 'inherit',
                      }}>
                        {item.label}
                      </span>
                      <ShortcutChip keys={item.shortcut} />
                    </>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* The door to your own employee experience. Sits below the navigation
          rather than in it: it is a change of place, not another destination
          within this one. Renders nothing unless you have an active employee
          record — see MyEmployeeViewLink. */}
      <div style={{ marginTop: 'auto', padding: collapsed ? '8px 6px' : '8px 10px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <MyEmployeeViewLink collapsed={collapsed} />
      </div>

    </>
  )
}
